/**
 * Orders — order book, broker sync, modify/cancel and the order ticket.
 *
 * Polls GET /orders every 5s. When a broker session is active it also calls
 * POST /orders/sync every other poll so fills, rejections and orders placed
 * from the broker's own app show up here. Placement goes through OrderTicket.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Slash,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, getErrorMessage } from '@/config/api';
import { cn } from '@/lib/cn';
import { formatDateTime, formatINR, formatQty, formatTime, shortId } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  SkeletonRows,
  StatCard,
  Table,
  TableMessage,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Tabs,
} from '@/components/ui';
import type { Tone } from '@/components/ui';
import { OrderTicket } from '@/features/orders/components/OrderTicket';
import type { OrderTicketPrefill } from '@/features/orders/components/OrderTicket';

// ── Types ───────────────────────────────────────────────────────────────────

interface OrderData {
  id: string;
  connection_id: string;
  exchange_segment: string;
  trading_symbol: string;
  instrument_token: string;
  transaction_type: string;
  order_type: string;
  product: string;
  validity: string;
  quantity: number;
  price: string;
  trigger_price: string;
  status: string;
  filled_quantity: number;
  pending_quantity: number;
  average_price: string;
  broker_order_id: string;
  risk_validated: boolean;
  rejection_reason: string;
  strategy_id: string;
  strategy_name: string;
  tag: string;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  filled_at: string | null;
  cancelled_at: string | null;
}

interface OrderEvent {
  event_id: string;
  order_id: string;
  event_type: string;
  payload: Record<string, unknown>;
  timestamp: string;
}

interface BrokerStatus {
  connection_id: string;
  display_name: string;
  is_active: boolean;
  is_session_valid: boolean;
}

interface SyncResult {
  broker_orders: number;
  checked: number;
  updated: number;
  imported: number;
  realized_pnl: string | null;
}

const modifySchema = z
  .object({
    quantity: z.coerce.number().int().positive('Must be > 0'),
    order_type: z.enum(['L', 'MKT', 'SL', 'SL-M']),
    price: z.coerce.number().min(0).default(0),
    trigger_price: z.coerce.number().min(0).default(0),
  })
  .refine((d) => !['L', 'SL'].includes(d.order_type) || d.price > 0, { message: 'Price required', path: ['price'] })
  .refine((d) => !['SL', 'SL-M'].includes(d.order_type) || d.trigger_price > 0, {
    message: 'Trigger required',
    path: ['trigger_price'],
  });

type ModifyForm = z.infer<typeof modifySchema>;

// ── Status vocabulary ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; tone: Tone; Icon: LucideIcon }> = {
  created: { label: 'Created', tone: 'neutral', Icon: Clock },
  sent: { label: 'Sent', tone: 'info', Icon: Send },
  accepted: { label: 'Open', tone: 'info', Icon: CheckCircle2 },
  partial: { label: 'Partial', tone: 'warning', Icon: Loader2 },
  filled: { label: 'Filled', tone: 'success', Icon: CheckCircle2 },
  rejected: { label: 'Rejected', tone: 'danger', Icon: XCircle },
  cancelled: { label: 'Cancelled', tone: 'neutral', Icon: Slash },
  expired: { label: 'Expired', tone: 'warning', Icon: Clock },
  modified: { label: 'Modified', tone: 'brand', Icon: RefreshCw },
  trigger_pending: { label: 'Trigger pending', tone: 'warning', Icon: AlertTriangle },
};

const ACTIVE_STATUSES = ['created', 'sent', 'accepted', 'partial', 'modified', 'trigger_pending'];

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Open' },
  { id: 'filled', label: 'Filled' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'cancelled', label: 'Cancelled' },
] as const;

type FilterId = (typeof STATUS_FILTERS)[number]['id'];

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.created;
  return (
    <Badge tone={cfg.tone} icon={cfg.Icon}>
      {cfg.label}
    </Badge>
  );
}

function describeEvent(evt: OrderEvent): string {
  const p = evt.payload || {};
  switch (evt.event_type) {
    case 'OrderRiskRejected':
      return Array.isArray(p.violations) ? (p.violations as string[]).join(', ') : '';
    case 'OrderSent':
      return p.broker_order_id ? `Broker #${p.broker_order_id}` : '';
    case 'OrderFilled':
    case 'OrderPartiallyFilled':
      return `${formatQty(p.filled_qty as number)} @ ${formatINR(p.fill_price as string)} · avg ${formatINR(p.average_price as string)}`;
    case 'OrderRejected':
      return String(p.reason ?? '');
    case 'OrderReconciled':
      return `${p.from} → ${p.to}${p.reason ? ` · ${p.reason}` : ''} (${p.source})`;
    case 'OrderImported':
      return `Found at broker as ${p.status}`;
    case 'OrderModified':
      return `qty ${formatQty(p.new_quantity as number)} @ ${formatINR(p.new_price as string)}`;
    default:
      return '';
  }
}

const COLUMN_COUNT = 9;
const POLL_MS = 5_000;

export default function OrdersPage() {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterId>('all');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [orderEvents, setOrderEvents] = useState<OrderEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState<OrderTicketPrefill | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; message: string } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<OrderData | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [modifyTarget, setModifyTarget] = useState<OrderData | null>(null);
  const [modifying, setModifying] = useState(false);
  const [connections, setConnections] = useState<BrokerStatus[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<{ at: Date; result: SyncResult } | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const pollCountRef = useRef(0);

  const activeConnection = useMemo(
    () => connections.find((c) => c.is_active && c.is_session_valid) ?? null,
    [connections]
  );

  // ── Fetch ─────────────────────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (activeFilter !== 'all' && activeFilter !== 'active') params.status = activeFilter;
      const { data } = await api.get('/orders', { params: { ...params, limit: 200 } });
      setOrders(data);
      setLoadError(null);
    } catch (err) {
      // Surfaced rather than swallowed: an empty table must not read as "no orders".
      setLoadError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [activeFilter]);

  const fetchConnections = useCallback(async () => {
    try {
      const { data } = await api.get('/brokers/status');
      setConnections(data);
    } catch {
      setConnections([]);
    }
  }, []);

  const syncWithBroker = useCallback(
    async (silent = false) => {
      if (!activeConnection) return;
      if (!silent) setSyncing(true);
      try {
        const { data } = await api.post('/orders/sync', { connection_id: activeConnection.connection_id });
        setLastSync({ at: new Date(), result: data });
        setSyncError(null);
      } catch (err) {
        setSyncError(getErrorMessage(err));
      } finally {
        if (!silent) setSyncing(false);
      }
    },
    [activeConnection]
  );

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(async () => {
      pollCountRef.current += 1;
      // Reconcile with the broker every other poll (~10s) to stay within API limits.
      if (pollCountRef.current % 2 === 0) await syncWithBroker(true);
      fetchOrders();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchOrders, syncWithBroker]);

  const fetchEvents = async (orderId: string) => {
    setEventsLoading(true);
    try {
      const { data } = await api.get(`/orders/${orderId}/history`);
      setOrderEvents(data);
    } catch {
      setOrderEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const toggleExpand = (orderId: string) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
      setOrderEvents([]);
    } else {
      setExpandedOrder(orderId);
      fetchEvents(orderId);
    }
  };

  const openTicket = (prefill: OrderTicketPrefill | null = null) => {
    setTicketPrefill(prefill);
    setTicketOpen(true);
  };

  // ── Cancel ────────────────────────────────────────────────────────────

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await api.delete(`/orders/${cancelTarget.id}`);
      setNotice({ ok: true, message: data.message || `Cancelled ${cancelTarget.trading_symbol}` });
      setCancelTarget(null);
      fetchOrders();
    } catch (err) {
      setNotice({ ok: false, message: getErrorMessage(err) });
      setCancelTarget(null);
    } finally {
      setCancelling(false);
    }
  };

  // ── Modify ────────────────────────────────────────────────────────────

  const {
    register: registerModify,
    handleSubmit: handleModifySubmit,
    reset: resetModify,
    watch: watchModify,
    formState: { errors: modifyErrors },
  } = useForm<ModifyForm>({ resolver: zodResolver(modifySchema) as never });

  const modifyType = watchModify('order_type');

  const openModify = (order: OrderData) => {
    resetModify({
      quantity: order.quantity,
      order_type: order.order_type as ModifyForm['order_type'],
      price: Number(order.price) || 0,
      trigger_price: Number(order.trigger_price) || 0,
    });
    setModifyTarget(order);
  };

  const submitModify = async (data: ModifyForm) => {
    if (!modifyTarget) return;
    setModifying(true);
    try {
      const { data: res } = await api.patch(`/orders/${modifyTarget.id}`, {
        quantity: data.quantity,
        order_type: data.order_type,
        price: ['L', 'SL'].includes(data.order_type) ? data.price : 0,
        trigger_price: ['SL', 'SL-M'].includes(data.order_type) ? data.trigger_price : 0,
      });
      if (res.risk_rejected) {
        setNotice({ ok: false, message: `Risk engine rejected the change: ${(res.violations || []).join(', ')}` });
      } else {
        setNotice({ ok: true, message: res.message || `Modified ${modifyTarget.trading_symbol}` });
      }
      setModifyTarget(null);
      fetchOrders();
    } catch (err) {
      setNotice({ ok: false, message: getErrorMessage(err) });
    } finally {
      setModifying(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const visibleOrders = useMemo(
    () => (activeFilter === 'active' ? orders.filter((o) => ACTIVE_STATUSES.includes(o.status)) : orders),
    [orders, activeFilter]
  );

  const stats = useMemo(() => {
    const active = orders.filter((o) => ACTIVE_STATUSES.includes(o.status));
    return {
      total: orders.length,
      active: active.length,
      filled: orders.filter((o) => o.status === 'filled').length,
      rejected: orders.filter((o) => o.status === 'rejected').length,
    };
  }, [orders]);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={FileText}
        title="Orders"
        description="Order book, execution status and risk audit trail"
        meta={
          <>
            {activeConnection ? (
              <Badge tone="success">{activeConnection.display_name}</Badge>
            ) : (
              <Badge tone="danger">No broker session</Badge>
            )}
            {lastSync && (
              <span className="text-xs text-fg-subtle">
                Synced {formatTime(lastSync.at.toISOString())}
                {lastSync.result.updated > 0 && ` · ${lastSync.result.updated} updated`}
                {lastSync.result.imported > 0 && ` · ${lastSync.result.imported} imported`}
              </span>
            )}
          </>
        }
        actions={
          <>
            {activeConnection && (
              <Button variant="secondary" size="sm" icon={RefreshCw} loading={syncing} onClick={() => syncWithBroker()}>
                Sync broker
              </Button>
            )}
            <Button variant="secondary" size="sm" icon={RefreshCw} onClick={fetchOrders}>
              Refresh
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => openTicket()}>
              New order
            </Button>
          </>
        }
      />

      {notice && (
        <Alert tone={notice.ok ? 'success' : 'danger'} onDismiss={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      {syncError && (
        <Alert tone="warning" title="Broker sync failed" onDismiss={() => setSyncError(null)}>
          {syncError}
        </Alert>
      )}

      {loadError && (
        <Alert
          tone="danger"
          title="Couldn't load orders"
          action={
            <Button variant="secondary" size="sm" onClick={fetchOrders}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard label="Total orders" value={stats.total} loading={isLoading} />
        <StatCard label="Open" value={stats.active} loading={isLoading} icon={Clock} />
        <StatCard label="Filled" value={stats.filled} loading={isLoading} icon={CheckCircle2} />
        <StatCard label="Rejected" value={stats.rejected} loading={isLoading} icon={XCircle} />
      </div>

      <Card>
        <div className="border-b border-line px-4 py-3">
          <Tabs
            aria-label="Filter orders by status"
            items={STATUS_FILTERS.map((f) => ({ id: f.id, label: f.label }))}
            value={activeFilter}
            onChange={(id) => setActiveFilter(id)}
          />
        </div>

        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH className="w-8" aria-label="Expand" />
                <TH>Instrument</TH>
                <TH>Side</TH>
                <TH>Type</TH>
                <TH align="right">Qty</TH>
                <TH align="right">Price</TH>
                <TH>Status</TH>
                <TH>Placed</TH>
                <TH align="right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {isLoading ? (
                <SkeletonRows rows={5} cols={COLUMN_COUNT} />
              ) : visibleOrders.length === 0 ? (
                <TableMessage colSpan={COLUMN_COUNT}>
                  <EmptyState
                    icon={FileText}
                    title={activeFilter === 'all' ? 'No orders yet' : `No ${activeFilter} orders`}
                    description={
                      activeFilter === 'all'
                        ? 'Orders you place — here or in your broker app — appear with their full risk and execution history.'
                        : 'Try a different status filter.'
                    }
                    action={
                      activeFilter === 'all' ? (
                        <Button variant="secondary" size="sm" icon={Plus} onClick={() => openTicket()}>
                          Place an order
                        </Button>
                      ) : undefined
                    }
                  />
                </TableMessage>
              ) : (
                visibleOrders.map((order) => {
                  const expanded = expandedOrder === order.id;
                  const buy = order.transaction_type === 'B';
                  const live = ACTIVE_STATUSES.includes(order.status);
                  const canModify = live && Boolean(order.broker_order_id) && order.status !== 'created';

                  return (
                    <Fragment key={order.id}>
                      <TR>
                        <TD>
                          <button
                            type="button"
                            onClick={() => toggleExpand(order.id)}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Hide' : 'Show'} history for ${order.trading_symbol}`}
                            className="rounded p-1 text-fg-faint transition-colors hover:bg-surface-2 hover:text-fg"
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" aria-hidden="true" />
                            ) : (
                              <ChevronRight className="h-4 w-4" aria-hidden="true" />
                            )}
                          </button>
                        </TD>
                        <TD>
                          <div className="flex items-center gap-1.5 font-medium text-fg">
                            {order.trading_symbol}
                            {order.strategy_name && (
                              <span title={`Placed by strategy ${order.strategy_name}`}>
                                <Bot className="h-3.5 w-3.5 text-brand-fg" aria-hidden="true" />
                                <span className="sr-only">Strategy order</span>
                              </span>
                            )}
                          </div>
                          <div className="font-tabular text-xs text-fg-faint uppercase">
                            {order.exchange_segment?.replace('_', ' ')}
                            {order.broker_order_id && ` · #${order.broker_order_id}`}
                          </div>
                        </TD>
                        <TD>
                          <span className={cn('text-xs font-semibold', buy ? 'text-buy' : 'text-sell')}>
                            {buy ? 'BUY' : 'SELL'}
                          </span>
                        </TD>
                        <TD>
                          <div className="text-xs text-fg-muted">{order.order_type}</div>
                          <div className="text-xs text-fg-faint">{order.product}</div>
                        </TD>
                        <TD align="right" className="font-tabular">
                          <div className="text-fg">{formatQty(order.quantity)}</div>
                          {order.filled_quantity > 0 && order.filled_quantity < order.quantity && (
                            <div className="text-xs text-fg-faint">{formatQty(order.filled_quantity)} filled</div>
                          )}
                        </TD>
                        <TD align="right" className="font-tabular">
                          <div className="text-fg">
                            {Number(order.price) > 0 ? formatINR(order.price) : order.order_type === 'MKT' ? 'MKT' : '—'}
                          </div>
                          {Number(order.average_price) > 0 && (
                            <div className="text-xs text-fg-faint">avg {formatINR(order.average_price)}</div>
                          )}
                          {Number(order.trigger_price) > 0 && (
                            <div className="text-xs text-fg-faint">trg {formatINR(order.trigger_price)}</div>
                          )}
                        </TD>
                        <TD>
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={order.status} />
                            {order.risk_validated ? (
                              <span title="Risk validated">
                                <ShieldCheck className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                                <span className="sr-only">Risk validated</span>
                              </span>
                            ) : (
                              <span title={order.tag === 'broker' ? 'Placed outside this platform' : 'Risk not validated'}>
                                <ShieldAlert className="h-3.5 w-3.5 text-fg-faint" aria-hidden="true" />
                                <span className="sr-only">Risk not validated</span>
                              </span>
                            )}
                          </div>
                          {order.rejection_reason && (
                            <div className="mt-1 max-w-56 text-xs break-words whitespace-normal text-danger">
                              {order.rejection_reason}
                            </div>
                          )}
                        </TD>
                        <TD className="font-tabular text-xs text-fg-subtle">{formatDateTime(order.created_at)}</TD>
                        <TD align="right">
                          <div className="flex justify-end gap-1.5">
                            {canModify && (
                              <Button variant="secondary" size="sm" icon={Pencil} onClick={() => openModify(order)}>
                                Modify
                              </Button>
                            )}
                            {live && (
                              <Button variant="danger" size="sm" onClick={() => setCancelTarget(order)}>
                                Cancel
                              </Button>
                            )}
                            {!live && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  openTicket({
                                    exchange_segment: order.exchange_segment,
                                    trading_symbol: order.trading_symbol,
                                    instrument_token: order.instrument_token,
                                    transaction_type: order.transaction_type as 'B' | 'S',
                                    product: order.product as 'CNC' | 'MIS' | 'NRML',
                                  })
                                }
                              >
                                Reorder
                              </Button>
                            )}
                          </div>
                        </TD>
                      </TR>

                      {expanded && (
                        <TR>
                          <TD colSpan={COLUMN_COUNT} className="bg-surface-2 whitespace-normal">
                            <div className="px-2 py-1">
                              <p className="mb-3 text-xs font-medium tracking-wider text-fg-faint uppercase">
                                Event trail · {shortId(order.id)}
                                {order.strategy_name && ` · ${order.strategy_name}`}
                              </p>
                              {eventsLoading ? (
                                <p className="text-xs text-fg-subtle">Loading history…</p>
                              ) : orderEvents.length === 0 ? (
                                <p className="text-xs text-fg-subtle">No events recorded for this order.</p>
                              ) : (
                                <ol className="flex flex-col gap-2.5">
                                  {orderEvents.map((evt) => {
                                    const detail = describeEvent(evt);
                                    return (
                                      <li key={evt.event_id} className="flex items-start gap-2.5">
                                        <span
                                          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand"
                                          aria-hidden="true"
                                        />
                                        <div>
                                          <p className="text-xs font-medium text-fg">{evt.event_type}</p>
                                          {detail && <p className="text-xs text-fg-muted">{detail}</p>}
                                          <p className="font-tabular text-xs text-fg-faint">
                                            {formatDateTime(evt.timestamp)}
                                          </p>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ol>
                              )}
                            </div>
                          </TD>
                        </TR>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableWrap>
      </Card>

      <OrderTicket
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        prefill={ticketPrefill}
        onPlaced={() => {
          fetchOrders();
          fetchConnections();
        }}
      />

      {/* Modify */}
      <Modal
        open={modifyTarget !== null}
        onClose={() => setModifyTarget(null)}
        title={modifyTarget ? `Modify ${modifyTarget.trading_symbol}` : 'Modify order'}
        description="The change is re-validated by the risk engine and sent to the broker."
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModifyTarget(null)}>
              Keep as is
            </Button>
            <Button type="submit" form="modify-order-form" variant="primary" loading={modifying}>
              Apply changes
            </Button>
          </>
        }
      >
        <form id="modify-order-form" onSubmit={handleModifySubmit(submitModify)} className="grid grid-cols-2 gap-4" noValidate>
          <Field label="Quantity" error={modifyErrors.quantity?.message} required>
            {(field) => <Input {...field} type="number" min={1} mono {...registerModify('quantity')} />}
          </Field>
          <Field label="Order type" error={modifyErrors.order_type?.message}>
            {(field) => (
              <Select {...field} {...registerModify('order_type')}>
                <option value="L">Limit</option>
                <option value="MKT">Market</option>
                <option value="SL">Stop loss</option>
                <option value="SL-M">Stop loss — market</option>
              </Select>
            )}
          </Field>
          <Field label="Price" error={modifyErrors.price?.message}>
            {(field) => (
              <Input
                {...field}
                type="number"
                step="0.05"
                min={0}
                mono
                disabled={!['L', 'SL'].includes(modifyType)}
                {...registerModify('price')}
              />
            )}
          </Field>
          <Field label="Trigger price" error={modifyErrors.trigger_price?.message}>
            {(field) => (
              <Input
                {...field}
                type="number"
                step="0.05"
                min={0}
                mono
                disabled={!['SL', 'SL-M'].includes(modifyType)}
                {...registerModify('trigger_price')}
              />
            )}
          </Field>
        </form>
      </Modal>

      {/* Cancel confirmation */}
      <Modal
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title="Cancel this order?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelTarget(null)}>
              Keep order
            </Button>
            <Button variant="danger" loading={cancelling} onClick={confirmCancel}>
              Cancel order
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          {cancelTarget && (
            <>
              {cancelTarget.transaction_type === 'B' ? 'Buy' : 'Sell'}{' '}
              <span className="font-tabular">{formatQty(cancelTarget.quantity)}</span>{' '}
              <span className="font-medium text-fg">{cancelTarget.trading_symbol}</span>
              {Number(cancelTarget.price) > 0 && (
                <>
                  {' '}
                  at <span className="font-tabular">{formatINR(cancelTarget.price)}</span>
                </>
              )}
              . The cancel request is sent to your broker.
            </>
          )}
        </p>
      </Modal>
    </div>
  );
}
